import { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useSipStore, type SipState } from '../stores/sipStore';
import { useLiveCall } from '../stores/liveCall';
import { useCustomerCard } from '../stores/customerCard';

// כשהדפדפן עצמו מנהל את השיחה (JsSIP) הסופטפון כבר מציג אותה — הפס הזה
// מיועד רק לשיחות שהדפדפן לא יודע עליהן: טלפון שולחני, רענון באמצע שיחה,
// שיחת קמפיין שה-ARI חיבר ישירות.
const SIP_KNOWS: SipState[] = ['ringing-in', 'ringing-out', 'on-call', 'on-hold'];

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * פס קבוע מתחת ל-Topbar: "יש לך שיחה פעילה" — מספר, כיוון, משך, ניתוק.
 * מוזן מהשרת (LiveCallTracker) ולכן שורד רענון ומופיע גם כשהשיחה רצה
 * במכשיר אחר של אותו נציג.
 */
export function ActiveCallBanner() {
  const active = useLiveCall((s) => s.active);
  const hangup = useLiveCall((s) => s.hangup);
  const sipState = useSipStore((s) => s.state);
  const showCard = useCustomerCard((s) => s.show);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);

  const visible = !!active && !SIP_KNOWS.includes(sipState);

  useEffect(() => {
    if (!visible) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [visible]);

  if (!visible || !active) return null;

  const answered = active.status === 'answered' && !!active.answered_at;
  const anchor = active.answered_at ?? active.started_at;
  const elapsed = anchor ? Math.max(0, Math.floor((now - new Date(anchor).getTime()) / 1000)) : 0;
  const inbound = active.direction === 'inbound';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="active-call-banner"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '6px 16px',
        background: answered ? 'var(--ok-soft, var(--accent-soft))' : 'var(--accent-soft)',
        color: 'var(--text)', borderBottom: '1px solid var(--line)', fontSize: 'var(--t-sm)',
      }}
    >
      <span className={answered ? undefined : 'ringing'} style={{ display: 'inline-flex', color: answered ? 'var(--ok)' : 'var(--accent-ink)' }}>
        <Icon name={inbound ? 'phoneIn' : 'phoneOut'} size={16} />
      </span>
      <span style={{ fontWeight: 600 }}>
        {answered ? 'שיחה פעילה' : inbound ? 'שיחה נכנסת מצלצלת' : 'מחייג'}
      </span>
      <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{active.remote || '—'}</span>
      {active.did && (
        <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
          {inbound ? 'אל' : 'מ'} <span dir="ltr">{active.did}</span>
        </span>
      )}
      <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>
        {fmt(elapsed)}
      </span>
      {active.source === 'call_row' && (
        <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }} title="מזוהה לפי רשומת השיחה, לא לפי ה-PBX">
          לפי רשומה
        </span>
      )}
      <span style={{ flex: 1 }} />
      {active.remote && (
        <button type="button" className="btn ghost" style={{ padding: '2px 10px' }} onClick={() => showCard(active.remote)}>
          <Icon name="user" size={13} /> כרטיס לקוח
        </button>
      )}
      <button
        type="button"
        className="btn danger"
        style={{ padding: '2px 10px' }}
        disabled={busy}
        aria-label="נתק"
        onClick={async () => {
          setBusy(true);
          await hangup('active');
          setBusy(false);
        }}
      >
        <Icon name="hang" size={13} /> נתק
      </button>
    </div>
  );
}
